import React, {Fragment} from "react";
import {useIntl} from "react-intl";

import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import {AiEditIcon, AiThumbDownIcon, AiThumbUpIcon} from "../../../common/svgIcons/svgIcons";

// One block of icon controls (thumb up / thumb down / edit) opening the custom prompt block
// (AIResultCustomPrompt) for a single piece of a "multiImage" AI result (see isMultiImage in
// aiApi) - rendered in the bottom right corner of every cell of the 2x2 grid (LookAIMultiImage)
// and, once a piece is opened, of the zoomed piece itself.
//
// Only multiImage results use these icon buttons: every other result gets the same edit action
// as a plain text button next to "download" - see LookAIImageOverlayImageContent.

type Props = {
  pieceIndex: number,
  onEditClick: (pieceIndex: number) => void,
  // only passed for the zoomed piece (viewOnly) - renders a close button after "edit" that
  // collapses the piece back into the grid
  onCloseClick?: (pieceIndex: number) => void,
  onThumbUpClick?: (pieceIndex: number) => void,
  onThumbDownClick?: (pieceIndex: number) => void,
  disabled?: boolean,
  className?: string,
};

export default function AIResultCustomPromptButtons({
  pieceIndex,
  onEditClick,
  onCloseClick,
  onThumbUpClick,
  onThumbDownClick,
  disabled,
  className,
}: Props) {

  const intl = useIntl();

  // the grid itself is click-through so a click lands on the piece underneath - the buttons
  // must take their own clicks back, and must not bubble up into the piece's onClick
  const handle = (callback?: (pieceIndex: number) => void) => (event: React.MouseEvent) => {
    event.stopPropagation();
    if (callback) {
      callback(pieceIndex);
    }
  };

  const showThumbsButtons = false;

  return (
    <div className={"flex flex--center u-gap-05 look-ai__overlay-pieces-buttons " + (className || "")}>
      {showThumbsButtons ? (
        <Fragment>
          <IconButton aria-label={intl.formatMessage({id: "iconButton.thumbUp"})}
                      className="look-icon look-ai__overlay-pieces-button"
                      disabled={disabled}
                      onClick={handle(onThumbUpClick)}>
            {AiThumbUpIcon}
          </IconButton>
          <IconButton aria-label={intl.formatMessage({id: "iconButton.thumbDown"})}
                      className="look-icon look-ai__overlay-pieces-button"
                      disabled={disabled}
                      onClick={handle(onThumbDownClick)}>
            {AiThumbDownIcon}
          </IconButton>
        </Fragment>
      ) : null}
      <IconButton aria-label={intl.formatMessage({id: "iconButton.edit"})}
                  className="look-icon look-ai__overlay-pieces-button"
                  disabled={disabled}
                  onClick={handle(onEditClick)}>
        {AiEditIcon}
      </IconButton>
      {onCloseClick ? (
        <IconButton aria-label={intl.formatMessage({id: "iconButton.close"})}
                    className="look-icon look-ai__overlay-pieces-button"
                    disabled={disabled}
                    onClick={handle(onCloseClick)}>
          <CloseIcon/>
        </IconButton>
      ) : null}
    </div>
  );
}
